import type { ReactNode } from "react";
import { ArrowRight, Building2, CarFront, Sparkles } from "lucide-react";
import { LinkButton } from "@/components/ui/LinkButton";

/**
 * Smoothly scroll to the in-page "How it works" section. The element id is a
 * static literal (never user-controlled), and the optional chaining keeps it a
 * no-op when the section is not mounted (e.g. the standalone Storybook story).
 */
function scrollToHowItWorks() {
  document
    .getElementById("how-it-works")
    ?.scrollIntoView({ behavior: "smooth" });
}

/**
 * The public homepage hero — a dual-audience funnel that converts BOTH
 * transportation companies and independent drivers as co-equal customers.
 *
 * Two-column "Operations Console" layout: a high-contrast left column (eyebrow,
 * the page's single `<h1>`, dual-audience subhead, two equal-weight CTAs, and a
 * tertiary "See how it works" jump) over a faint cobalt grid/glow atmosphere,
 * and a right `visual` slot. Stacks to one column on mobile; flip-safe in both
 * light and dark themes; token classes only.
 *
 * Self-contained and presentational — the integration unit supplies
 * `visual={<DepartureBoard />}` and renders the `#how-it-works` target.
 *
 * @param root0 - Component props
 * @param root0.visual - Right-column visual slot (e.g. the departure board)
 * @returns The homepage hero section
 */
export function HomeHero({ visual }: { visual?: ReactNode }) {
  return (
    <section className="relative overflow-hidden border-b border-neutral-200 bg-background">
      {/* Atmosphere: a faint cobalt dot-grid plus a soft radial glow anchored to
          the headline. Token-driven (currentColor / semantic vars) so it flips
          with the theme; purely decorative, kept out of the a11y tree. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 text-primary-600/[0.06] [background-image:radial-gradient(currentColor_1px,transparent_1px)] [background-size:22px_22px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-primary-500/10 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:items-center lg:gap-12 lg:py-24 lg:px-8">
        {/* Left: the conversion column */}
        <div className="max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-surface px-3 py-1 font-mono text-xs font-medium uppercase tracking-[0.18em] text-neutral-600">
            <span
              aria-hidden="true"
              className="size-1.5 rounded-full bg-live-500 motion-safe:animate-pulse"
            />
            Live dispatch &amp; reservations
          </span>

          {/* The single page <h1>. Strong, stable, AI-native positioning; the
              integration unit reads this exact text for the e2e assertion. */}
          <h1 className="mt-6 text-balance text-4xl font-bold leading-[1.05] tracking-tight text-neutral-900 sm:text-5xl lg:text-6xl">
            The intelligent operating system for ground transportation
          </h1>

          <p className="mt-6 max-w-lg text-pretty text-lg text-neutral-600">
            One AI-native console that books, dispatches, and tracks every ride
            in real time — purpose-built for{" "}
            <span className="font-medium text-neutral-900">
              transportation operators
            </span>{" "}
            and{" "}
            <span className="font-medium text-neutral-900">
              independent drivers
            </span>{" "}
            alike.
          </p>

          {/* Two co-equal CTAs of equal visual weight (both filled-primary),
              one per audience. LinkButton = a react-router Link styled as a
              Button, so navigation stays inside the SPA. */}
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <LinkButton
              to="/register?type=business"
              size="lg"
              className="w-full sm:w-auto"
            >
              <Building2
                className="mr-2 h-4 w-4 shrink-0"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              Get started
              <ArrowRight
                className="ml-2 h-4 w-4 shrink-0"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            </LinkButton>
            <LinkButton
              to="/register?type=driver"
              size="lg"
              className="w-full sm:w-auto"
            >
              <CarFront
                className="mr-2 h-4 w-4 shrink-0"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              I&apos;m a driver
            </LinkButton>
          </div>

          {/* Tertiary, low-emphasis jump to the in-page section. */}
          <button
            type="button"
            onClick={scrollToHowItWorks}
            className="mt-4 inline-flex min-h-11 items-center gap-1.5 rounded-sm text-sm font-medium text-neutral-600 transition-colors pointer-fine:min-h-9 hover:text-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Sparkles
              className="h-3.5 w-3.5 shrink-0"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            See how it works
          </button>
        </div>

        {/* Right: the visual slot (e.g. the live departure board). */}
        <div className="lg:justify-self-end lg:[width:min(100%,38rem)]">
          {visual}
        </div>
      </div>
    </section>
  );
}
